import {routePathAdapter} from './route-path-adapter'
import type {RouteObject as ReactRouterRouteObject} from '../react-router'
import type {LoaderFnContext, AnyRoute, AnyContext, RouteComponent} from '@tanstack/react-router'
import {ssrSafeLocation} from '@github-ui/ssr-utils'

export type TanStackCreateRouteOptions = Parameters<typeof import('@tanstack/react-router').createRoute>[0]
export type TanStackRouteOptions = Pick<
  TanStackCreateRouteOptions,
  | 'loader'
  | 'component'
  | 'errorComponent'
  | 'beforeLoad'
  | 'validateSearch'
  | 'staticData'
  | 'loaderDeps'
  | 'ssr'
  | 'shouldReload'
>

declare module '@tanstack/react-router' {
  interface StaticDataRouteOption {
    reactRouterHandle?: ReactRouterRouteObject['handle']
  }
}

type RouteIdentifier = {path: string} | {id: string}
type RouteOptionsReturnType = TanStackRouteOptions & {
  normalizedRelativePath: string | undefined
  routeIdentifier: RouteIdentifier
}
export type GetRouteOptions = (routeObj: ReactRouterRouteObject) => TanStackRouteOptions

/**
 * Converts a React Router `RouteObject` into the options object passed to TanStack Router's
 * `createRoute`.
 *
 * The adapter auto-converts these TanStack route properties from the React Router route:
 *
 * - `loader`: adapted from `routeObj.loader`.
 * - `component`: adapted from `routeObj.Component` or `routeObj.element`.
 * - `errorComponent`: adapted from `routeObj.ErrorBoundary` or `routeObj.errorElement`.
 * - `beforeLoad`: initialized to return `{dataRouterId: routeObj.id}`.
 * - `shouldReload`: derived via `shouldReloadAdapter(...)`.
 * - `staticData`: initialized with `{reactRouterHandle: routeObj.handle}`.
 *
 * When `getRouteOptions` is provided, its return value is merged on top of the adapter-generated
 * options. `beforeLoad` and `staticData` are augmented rather than replaced: `beforeLoad` merges
 * the consumer result with the adapter's `{dataRouterId}` payload, and `staticData` shallow-merges
 * onto `{reactRouterHandle: routeObj.handle}`. Other overlapping properties override the adapter
 * value.
 *
 * The returned object includes the following additional properties for building the route tree:
 * - `routeIdentifier`: either `{path}` (for routable routes) or `{id}` (for pathless layout
 *   routes).
 * - `normalizedRelativePath`: the resolved relative path, used by `routeTreeAdapter` to thread the
 *   correct parent path down to child routes before the route tree is fully assembled.
 */
export function routeOptionsAdapter(
  routeObj: ReactRouterRouteObject,
  parentFullPath: string,
  getRouteOptions?: GetRouteOptions,
): RouteOptionsReturnType {
  const normalizedRelativePath = routePathAdapter(routeObj, parentFullPath)

  const routeIdentifier: {path: string} | {id: string} = normalizedRelativePath
    ? {path: normalizedRelativePath}
    : {id: routeObj.id ?? `${parentFullPath}/_layout`}

  const customOptions = getRouteOptions ? getRouteOptions(routeObj) : {}

  const adapterOptions = {
    loader: loaderAdapter(routeObj),
    beforeLoad: () => ({dataRouterId: routeObj.id}),
    shouldReload: shouldReloadAdapter(routeObj, customOptions.shouldReload),
    component: componentAdapter(routeObj),
    errorComponent: errorComponentAdapter(routeObj),
    staticData: {reactRouterHandle: routeObj.handle},
    routeIdentifier,
    normalizedRelativePath,
  }
  if (getRouteOptions) {
    // Merge consumer-provided route options with adapter-generated options.
    const {staticData: customStaticData, beforeLoad: customBeforeLoad, ...otherCustomOptions} = customOptions
    return {
      ...adapterOptions,
      ...(customStaticData ? {staticData: {...adapterOptions.staticData, ...customStaticData}} : {}),
      ...(customBeforeLoad ? {beforeLoad: beforeLoadAdapter(adapterOptions.beforeLoad, customBeforeLoad)} : {}),
      ...otherCustomOptions,
    }
  }

  return adapterOptions
}

type AnyLoaderContext = LoaderFnContext<
  unknown,
  AnyRoute,
  string,
  unknown,
  Record<string, unknown>,
  AnyContext,
  unknown,
  unknown
>
export function loaderAdapter(routeObj: ReactRouterRouteObject) {
  if (!routeObj.loader || typeof routeObj.loader !== 'function') {
    return undefined
  }

  const originalLoader = routeObj.loader

  return async (ctx: AnyLoaderContext) => {
    const {
      params,
      location,
      abortController: {signal},
      context,
    } = ctx

    const url = new URL(location.pathname + (location.searchStr ?? ''), ssrSafeLocation.origin)
    const request = new Request(url.toString(), {signal})

    const loaderArgs = {
      request,
      params,
      context,
      url,
      pattern: location.pathname,
      unstable_pattern: location.pathname,
    }

    return originalLoader(loaderArgs)
  }
}

export function componentAdapter(routeObj: ReactRouterRouteObject): RouteComponent | undefined {
  const Component = routeObj.Component
  if (Component) return Component as RouteComponent
  if (routeObj.element) return () => routeObj.element
  return undefined
}

export function errorComponentAdapter(routeObj: ReactRouterRouteObject): RouteComponent | undefined {
  const ErrorBoundary = routeObj.ErrorBoundary
  if (ErrorBoundary) return ErrorBoundary as RouteComponent
  if (routeObj.errorElement) return () => routeObj.errorElement
  return undefined
}

function beforeLoadAdapter(
  adapterBeforeLoad: NonNullable<TanStackRouteOptions['beforeLoad']>,
  customBeforeLoad: NonNullable<TanStackRouteOptions['beforeLoad']>,
): NonNullable<TanStackRouteOptions['beforeLoad']> {
  return params => {
    const beforeLoadResult = customBeforeLoad(params)
    // If the consumer's beforeLoad returns a promise, wait for it to resolve before
    // merging its result with the adapter's beforeLoad result.
    if (isPromise(beforeLoadResult)) {
      return (async () => {
        const resolvedBeforeLoadResult = await beforeLoadResult
        return {...adapterBeforeLoad(params), ...(resolvedBeforeLoadResult ?? {})}
      })()
    }
    return {...adapterBeforeLoad(params), ...(beforeLoadResult ?? {})}
  }
}

/**
 * This matches the guard used internally by TanStack Router to detect if beforeLoad returns a promise.
 * https://github.com/TanStack/router/blob/14520e5eb5f5a08026fdb46292fe35cf428a87aa/packages/router-core/src/load-matches.ts#L518
 */
function isPromise<T>(value: Promise<Awaited<T>> | T): value is Promise<Awaited<T>> {
  return Boolean(value && typeof value === 'object' && typeof (value as Promise<T>)['then'] === 'function')
}

/**
 * Passes through the consumer-provided TanStack Router `shouldReload` option.
 * `shouldReload` is optional unless the source React Router route defines
 * `shouldRevalidate`, in which case this adapter throws unless an explicit
 * `shouldReload` adapter is provided.
 *
 * React Router's `shouldRevalidate` receives a richer set of navigation and
 * submission details than TanStack Router's `shouldReload`, so there is no
 * safe generic adapter between the two. Routes that define
 * `routeObj.shouldRevalidate` must therefore provide an explicit
 * `shouldReload` option via the `getRouteOptions` callback.
 *
 * See:
 * https://tanstack.com/router/latest/docs/framework/react/api/router/RouteOptionsType#shouldreload-property
 */
function shouldReloadAdapter(
  routeObj: ReactRouterRouteObject,
  customShouldReload: TanStackRouteOptions['shouldReload'],
): TanStackRouteOptions['shouldReload'] {
  if (routeObj.shouldRevalidate && customShouldReload === undefined) {
    throw new Error(
      `Route with id "${routeObj.id}" has a shouldRevalidate function but no shouldReload option. ` +
        `React Router's shouldRevalidate cannot be automatically adapted for TanStack. ` +
        `Please configure shouldReload for this route.`,
    )
  }
  return customShouldReload
}
